import {
  PausedBy,
  useUpdateWorkflowTimeAndStateMutation,
  ZoneState,
} from '@/__generated__/graphql';
import { useEffect, useRef } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import * as state from '@/state';
import { useRouter } from './useRouter';
import { useGetStructureId } from './useGetStructureId';

export function useTimeTrackingUpdates(timeInMs: number) {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const router = useRouter();
  const zoneIdFromUrl = router?.query?.zoneid;
  const selectedZone = useAtomValue(state.selectedZone);
  const selectedZoneRef = useRef(selectedZone);
  const zoneId = zoneIdFromUrl ? zoneIdFromUrl.toString() : selectedZone?.id;
  const structureId = useGetStructureId();
  const [startTimeStamp, setStartTimeStamp] = useAtom(state.startTimeStampForTimeLogs);
  const startTimeStampRef = useRef(startTimeStamp);

  const [mutateWorkflowTimeAndState] = useUpdateWorkflowTimeAndStateMutation();

  useEffect(() => {
    const callMutation = () => {
      const assigneeId = selectedZoneRef.current?.workflowStatus?.assignedToUser?.id;
      const statusId = selectedZoneRef.current?.workflowStatus?.workflowStageId;
      const zoneState = selectedZoneRef.current?.workflowStatus?.state;
      const pausedBy = selectedZoneRef.current?.workflowStatus?.pausedBy;
      if (
        zoneId &&
        assigneeId &&
        statusId &&
        (zoneState === ZoneState.Start || zoneState === ZoneState.Pause)
      ) {
        if (zoneState === ZoneState.Pause && pausedBy === PausedBy.User) return;
        mutateWorkflowTimeAndState({
          variables: {
            input: {
              structureId,
              zoneId,
              assigneeId,
              typeOfTimeLog: zoneState === ZoneState.Start ? 'totalTime' : 'idleTime',
              timeInMs: new Date().getTime() - startTimeStampRef.current.getTime(),
              statusId,
              assignedToUser: selectedZoneRef.current?.workflowStatus?.assignedToUser,
            },
          },
        })
          .then(() => {
            setStartTimeStamp(new Date());
          })
          .catch((err) => {
            console.error('Mutation failed:', err);
          });
      }
    };

    intervalRef.current = setInterval(callMutation, timeInMs); // every 60s

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [mutateWorkflowTimeAndState, setStartTimeStamp, structureId, timeInMs, zoneId]);

  useEffect(() => {
    selectedZoneRef.current = selectedZone;
    startTimeStampRef.current = startTimeStamp;
  }, [selectedZone, startTimeStamp]);
}
